import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useInfiniteQueryBuilder, useQueryBuilder } from "../useBuilders";
import {
  DEAL_CHAT,
  DEAL_AGENT_SUGGESTIONS,
  FETCH,
  DEAL_AGENT_SESSIONS,
} from "../queryKeys";
import { dealAgentApi } from "src/services/dealInsights/dealAgent";
import { DealAgentChat } from "src/types/dealAgent";
import { defaultDealAgentChat, mergeStreamEventIntoChats } from "src/utils/dealAgent.helper";

export const useDealChatsFetch = (dealId?: string) => { // receive the same key to receive (subscribe to the chats)
  const key = [DEAL_CHAT, dealId ?? '']; 
  return useQuery<DealAgentChat[]>({
    queryKey: key,
    initialData: [],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useClearDealChats = () => { // clear the chats for a specific deal
  const queryClient = useQueryClient();

  return (dealId?: string) => {
    queryClient.setQueryData(
      [DEAL_CHAT, dealId ?? ''],
      []
    );
  };
};

export const useFetchSessions = (dealId: string) => { // sessions/history
  return useInfiniteQueryBuilder(
    [DEAL_AGENT_SESSIONS, FETCH, dealId ?? ""],
    ({ pageParam = 0 }) => dealId ? dealAgentApi.getSessions({ 'dealId': dealId, page: pageParam, limit: 20 }) : Promise.resolve({}),
    0,
    (response) => {
      const { totalPagesCount, pageNumber } = response?.pagination ?? {};
      const nextPage = pageNumber + 1 < totalPagesCount ? pageNumber + 1 : undefined;
      return nextPage;
    },
    (firstPage, allPages) => "",
    true,
    false,
  );
};


export const useFetchSessionChats = (dealId?: string) => { // chats
  const queryClient = useQueryClient();
  const key = [DEAL_CHAT, dealId ?? ''];

  return useMutation({
    mutationFn: async (sessionId: string) => { // fetch the chats for the specific session
      return dealAgentApi.getChats(sessionId);
    },

    onMutate: () => { // clear the chats for the specific deal
      queryClient.setQueryData(key, []);
    },

    onSuccess: (response) => { // set the chats for the specific session
      queryClient.setQueryData(key,
        response?.chats ?? []
      );
    },
  });
};

/*
 * Mutation that: (Server-Sent Events)
 * - Reads the SSE stream chunk by chunk. ()
 * - Calls queryClient.setQueryData on every chunk → triggers re-render via useDealAgentChats (receive the same key to receive the updated chats)
 * - On error → marks the optimistic chat as failed (creating: false)
 */
export const useCurrentDealChatStream = (dealId?: string) => { // use the mutation to ask the question
  const queryClient = useQueryClient();
  const key = [DEAL_CHAT, dealId ?? ''];

  return useMutation({
    mutationFn: async ({ chatId, question, sessionId }: { chatId: string; question: string; sessionId?: string }) => {
      await dealAgentApi.askQuestion(
        { question, sessionId, dealId },
        (event) => { // set the data to the query client
          queryClient.setQueryData<DealAgentChat[]>(key, (old = []) => {
            const updated = mergeStreamEventIntoChats(old, chatId, event); // merge the stream event into the chats list
            return updated;
          });
        }
      );
    },

    onMutate: ({ chatId, question }: { chatId: string; question: string }) => {
      queryClient.setQueryData<DealAgentChat[]>(key, (old = []) => {
        const chatData: DealAgentChat = {
          ...defaultDealAgentChat,
          id: chatId,
          question
        };
        return [...old, chatData];
      });
    },

    onError: (err: any, { chatId }) => {
      queryClient.setQueryData<DealAgentChat[]>(key, (old = []) =>
        old.map((chat) =>
          chat.id === chatId
            ? {
              ...chat,
              creating: false,
              error: {
                message: err?.message ?? "Something went wrong. Please try again.",
                code: err?.response?.status ?? 0,
              },
              events: [
                ...[...chat?.events ?? []],
                {'error': err?.message}, ] // add the error to the events (ui handling)
            }
            : chat)); // return the updated chat (current)
    },
  });
};
